import React from 'react';
import { Link } from 'react-router-dom';
import { User, Phone, ArrowRight } from 'lucide-react';
import { TEAM, COMPANY } from '../mock/data';
import { useT } from '../i18n/LanguageContext';
import { Reveal } from '../hooks/useReveal';
import SEO, { breadcrumbLd } from '../components/SEO';

const TeamPage = () => {
  const { t, lang } = useT();
  return (
    <div className="bg-white pt-32 pb-24">
      <SEO
        title="Team · Armin Achermann und Frachigers Handwerk"
        description="Lernen Sie das Team von Frachigers Handwerk kennen. Persönliche Ansprechpartner für alle Handwerksarbeiten in Seelisberg und der Zentralschweiz."
        path="/team"
        lang={lang}
        jsonLd={[breadcrumbLd([{ name: 'Home', path: '/' }, { name: 'Team', path: '/team' }])]}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="text-brand-red uppercase tracking-[0.3em] text-xs mb-3 font-semibold">{t('team.eyebrow')}</div>
            <h1 className="font-display text-5xl md:text-7xl text-neutral-900 leading-tight mb-6">
              {t('team.title')} <span className="italic text-brand-red">{t('team.titleEm')}</span>
            </h1>
            <p className="text-neutral-600 text-lg leading-relaxed">{t('team.subtitle')}</p>
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {TEAM.map((member, i) => (
            <Reveal key={member.id} delay={i * 150}>
              <div className="group bg-neutral-50 rounded-sm border border-neutral-200 hover:border-brand-blue overflow-hidden lift-on-hover">
                <div className="relative aspect-[4/5] bg-neutral-100 overflow-hidden flex items-center justify-center">
                  <div className="absolute inset-0 bg-gradient-to-br from-neutral-50 via-white to-neutral-100"></div>
                  <div className="relative flex flex-col items-center text-center px-6">
                    <div className="w-32 h-32 rounded-full border-2 border-neutral-300 bg-neutral-50 flex items-center justify-center mb-5">
                      <User className="w-16 h-16 text-neutral-400" />
                    </div>
                    <span className="text-neutral-400 text-[10px] uppercase tracking-[0.3em]">{t('team.photoSoon')}</span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-brand-red via-brand-gold to-brand-red"></div>
                </div>

                <div className="p-7">
                  <h3 className="font-display text-2xl text-neutral-900 mb-1">{member.nameKey}</h3>
                  <div className="text-brand-red text-xs uppercase tracking-[0.2em] font-semibold">{member.roleKey[lang]}</div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal>
          <div className="mt-16 bg-white p-8 md:p-12 rounded-sm border border-neutral-200 text-center">
            <div className="font-display text-2xl md:text-3xl text-neutral-900 mb-3">{t('team.teamPlay')}</div>
            <p className="text-neutral-600 max-w-2xl mx-auto mb-8">{t('team.teamPlaySub')}</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/kontakt" className="inline-flex items-center justify-center gap-2 bg-brand-blue hover:bg-brand-blueDark text-white px-6 py-3 text-sm font-semibold uppercase tracking-wider rounded-sm transition-colors btn-pulse-dark">
                {t('team.meetUs')} <ArrowRight className="w-4 h-4" />
              </Link>
              <a href={`tel:${COMPANY.phoneInternational}`} className="inline-flex items-center justify-center gap-2 border-2 border-brand-blue text-neutral-900 hover:bg-white hover:text-neutral-900 px-6 py-3 text-sm font-semibold uppercase tracking-wider rounded-sm transition-colors">
                <Phone className="w-4 h-4" /> {t('common.call')}
              </a>
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  );
};

export default TeamPage;
